import { useAuth0 } from '@auth0/auth0-react'
import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import logo from '~/assets/images/logo_monomer.svg'
import Button from '~/components/buttons/Button'
import { FullPageError } from '~/components/Errors'
import cs from './login_page.scss'
import useLogout from './useLogout'

/**
 * Parse the URL for untrusted error callback parameters (for example, from
 * Auth0) and return a trusted, user-friendly error message.
 */
function useFriendlyAuth0ErrorFromURL(): {
  hasError: boolean
  friendlyMessage?: string
} {
  const location = useLocation()
  const params = new URLSearchParams(location.search)

  // NOTE: URL parameters are UNTRUSTED input. Any new cases must explicitly
  // match the error and description, and any calls-to-action should be reviewed
  // for safety. Never render arbitrary URL parameters into the UI, especially
  // in a sensitive flow like login.
  const errorUntrusted = params.get('error')
  const errorDescriptionUntrusted = params.get('error_description')

  if (!params.get('state')) {
    // Not an Auth0 error.
    return { hasError: false }
  }

  switch (errorUntrusted) {
    case null:
      // No error in URL
      return { hasError: false }

    case 'invalid_request':
      if (
        errorDescriptionUntrusted?.toLowerCase() ===
        'client requires organization membership, but user does not belong to any organization'
      ) {
        return {
          hasError: true,
          friendlyMessage:
            "You've signed in successfully, but you're not yet a member of any organizations. Please ask your Monomer support team to add you to an organization.",
        }
      }
      return { hasError: true }

    case 'access_denied':
      return { hasError: true }

    case 'unauthorized':
      if (errorDescriptionUntrusted?.startsWith('EMAIL_VERIFICATION_REQUIRED')) {
        return {
          hasError: true,
          friendlyMessage: `To finish setting up your account, please verify your email address. Look for an email with the subject "Verify your email." Once verified, log out and log in again.`,
        }
      }
      return { hasError: true }

    default:
      // We don't return a generic error message here, since it would be brittle
      // to block the entire app if there's ever a URL parameter called `error`.
      // Adding cases above should be easy enough. We can revisit this decision
      // if we notice users being confused by lots of different auth errors.
      return { hasError: false }
  }
}

function FullPageLoading() {
  return (
    <div className={cs.fullPageContainer}>
      <div className={cs.slowLoadingIndicator}>
        <img src={logo} alt='Monomer Bio logo' width={50} height={50} />
      </div>
      <div className={cs.slowLoadingMessage}>Loading...</div>
    </div>
  )
}

function FullPageAuthenticationError({
  customFriendlyMessage,
}: {
  /**
   * Be careful with the wording of this message. See `FullPageError` for
   * guidelines.
   */
  customFriendlyMessage?: string
}) {
  const { logout } = useLogout()

  return (
    <div className={cs.fullPageErrorContainer}>
      <FullPageError
        customFriendlyMessage={customFriendlyMessage}
        customActionButton={
          <Button
            onClick={() =>
              logout({
                logoutParams: { returnTo: window.location.origin },
              })
            }
            label='Log Out'
            type='primary'
          />
        }
      />
    </div>
  )
}

/**
 * Redirect to the Auth0 login page. If we've recently redirected, or if we're
 * already logged in, render a button instead, to avoid a redirect loop.
 */
function LoginPage() {
  const { loginWithRedirect, isAuthenticated, isLoading: isAuth0Loading } = useAuth0()
  const { hasError: hasAuth0Error, friendlyMessage: friendlyAuth0ErrorMessage } =
    useFriendlyAuth0ErrorFromURL()

  const KEY = 'monomer_login_redirected_at'
  const lastRedirectedAt = parseInt(localStorage.getItem(KEY) ?? '0', 10)
  const shouldRedirect =
    !isAuthenticated &&
    !isAuth0Loading &&
    !hasAuth0Error &&
    Date.now() - lastRedirectedAt > 10 * 1000

  useEffect(() => {
    if (shouldRedirect) {
      localStorage.setItem(KEY, Date.now().toString())
      loginWithRedirect()
    }
  }, [shouldRedirect, loginWithRedirect])

  if (hasAuth0Error) {
    return (
      <FullPageAuthenticationError
        customFriendlyMessage={friendlyAuth0ErrorMessage ?? undefined}
      />
    )
  }

  if (isAuth0Loading || shouldRedirect) {
    return <FullPageLoading />
  }

  return (
    <div className={cs.fullPageContainer}>
      <div className={cs.loggedOutMessage}>Log in to Monomer Bio to continue.</div>
      <Button onClick={() => loginWithRedirect()} label='Log In' type='primary' />
    </div>
  )
}

export default LoginPage
